import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { ChartFrame } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getCostIndex, getRecords, isCode, messageFor, type RecordRow } from "../api";
import { ENTITIES, pct, shortDate } from "../format";
import { basisWords, canDrill, costWords, historySeries, qtyWords, sortByDate } from "../records";
import { money } from "../format";
import { lineColor, monthShort } from "@/components/cost_index/palette";

const LINE_COLORS = ["var(--color-ks-purple)", "var(--color-ks-purple-1)", "var(--color-ks-purple-2)", "var(--color-ks-purple-3)"];

export type SkuHistorySelection = Pick<RecordRow, "sku" | "tracking" | "variant">;

export function SkuHistory({ selected, onClose, onProductionOpen }: {
  selected: SkuHistorySelection;
  onClose: () => void;
  onProductionOpen: (productionKey: string) => void;
}) {
  const history = useQuery({
    queryKey: ["pcogs", "records", "sku", selected.sku],
    queryFn: () => getRecords({ entity: "all", sku: selected.sku, limit: 5000 }),
  });
  const breakdown = useQuery({
    queryKey: ["pcogs", "cost-index", "sku-breakdown", selected.sku],
    queryFn: () => getCostIndex({ sku: [selected.sku], view: "total", weighting: "simple" }),
    retry: false,
  });
  const rows = sortByDate(history.data?.data ?? [], "desc");
  const lines = breakdown.data?.data;
  const lineDefs = [...new Map((lines?.series ?? []).flatMap((point) => point.lines).map((line) => [line.key, line])).values()];
  const linePoints = (lines?.series ?? []).map((point) => ({ month: point.month, ...Object.fromEntries(point.lines.map((line) => [line.key, line.unit_cost == null ? null : Number(line.unit_cost)])) }));
  const lineCurrency = lines?.scope.book_currency ?? null;
  const series = historySeries(rows);
  const title = `${selected.sku} · ${selected.tracking ?? "-"} · ${selected.variant ?? "-"}`;

  return <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent id="m-sku-history" className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Every recorded cost for this SKU across all entities.</DialogDescription>
      </DialogHeader>
      {history.isPending && <p>Loading SKU history...</p>}
      {history.error && <p role="alert" className="text-ks-danger">{messageFor(history.error)} <Button data-nonmutating variant="outline" size="sm" onClick={() => { void history.refetch(); }}>Retry</Button></p>}
      {!history.isPending && !history.error && <div className="space-y-6">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Entity</TableHead><TableHead>Production</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Ccy</TableHead><TableHead>Basis</TableHead><TableHead className="text-right">vs prev</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((row) => <TableRow key={row.record_id}>
            <TableCell>{shortDate(row.approved_date)}</TableCell>
            <TableCell>{ENTITIES[row.entity_id].short}</TableCell>
            <TableCell>{canDrill(row)
              ? <Button data-nonmutating variant="link" className="px-0 font-medium" onClick={() => onProductionOpen(row.production_key!)}>{row.production_key}</Button>
              : <Badge variant="outline">Legacy</Badge>}</TableCell>
            <TableCell className="text-right">{qtyWords(row)}</TableCell>
            <TableCell className="text-right">{costWords(row)}</TableCell>
            <TableCell>{row.book_currency}</TableCell>
            <TableCell>{basisWords(row)}</TableCell>
            <TableCell className="text-right">{pct(row.vs_previous_pct)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
        <section aria-label="Cost by account" className="space-y-2">
          <h3 className="text-sm font-semibold text-ks-ink">Cost per unit by account</h3>
          {breakdown.isPending && <p className="text-sm text-muted-foreground">Loading account breakdown...</p>}
          {breakdown.error && isCode(breakdown.error, "no_history") && <p className="text-sm text-muted-foreground">No cost lines recorded for this SKU yet.</p>}
          {breakdown.error && !isCode(breakdown.error, "no_history") && <p role="alert" className="text-sm text-ks-danger">{messageFor(breakdown.error)}</p>}
          {lines && lineCurrency && <>
            <Table>
              <TableHeader><TableRow><TableHead>Month</TableHead>{lineDefs.map((line) => <TableHead key={line.key} className="text-right">{line.label}</TableHead>)}<TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>{[...lines.series].reverse().map((point) => <TableRow key={point.month}>
                <TableCell>{monthShort(point.month)}{point.data_quality !== "measured" && <Badge variant="outline" className="ml-2">est. split</Badge>}</TableCell>
                {lineDefs.map((line) => <TableCell key={line.key} className="text-right">{money(point.lines.find((item) => item.key === line.key)?.unit_cost, lineCurrency, 4)}</TableCell>)}
                <TableCell className="text-right font-semibold">{money(point.total_unit_cost, lineCurrency, 4)}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
            <ChartFrame title={`${selected.sku} · ${lineCurrency} per unit by account`} titleVisible height={260} pointCount={linePoints.length}>
              <BarChart data={linePoints}>
                <XAxis dataKey="month" tickFormatter={monthShort} minTickGap={40} />
                <YAxis width={56} tickFormatter={(value) => money(Number(value), lineCurrency, 2)} />
                <Tooltip labelFormatter={(month) => monthShort(String(month))} formatter={(value, name) => [money(Number(value), lineCurrency, 4), String(name)]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {lineDefs.map((line, index) => <Bar key={line.key} dataKey={line.key} name={line.label} stackId="cost" fill={lineColor(line, index)} isAnimationActive={false} />)}
              </BarChart>
            </ChartFrame>
            <p className="text-xs text-muted-foreground">Switch to the COGS Index tab and search this SKU for Level 1, 2 and 3 splits.</p>
          </>}
        </section>
        <ChartFrame title={`${selected.sku} cost history`} titleVisible height={260} pointCount={series.points.length}>
          <LineChart data={series.points}>
            <XAxis dataKey="approved_date" />
            <YAxis />
            <Tooltip labelFormatter={(date) => String(date)} formatter={(value, name) => [`${Number(value).toFixed(4)} ${String(name)}`, String(name)]} />
            <Legend />
            {series.currencies.map((currency, index) => <Line key={currency} type="monotone" dataKey={currency} name={currency} stroke={LINE_COLORS[index % LINE_COLORS.length]} dot connectNulls isAnimationActive={false} />)}
          </LineChart>
        </ChartFrame>
      </div>}
    </DialogContent>
  </Dialog>;
}
